import {
  cache,
  Enums,
  imageLoader,
  RenderingEngine,
} from "@cornerstonejs/core";
import cornerstoneDICOMImageLoader from "@cornerstonejs/dicom-image-loader";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  DICOM_TAGS,
  getOppositeDirection,
  mapToLRAPIS,
  MODALITY,
} from "../../../classes/enums/MedicalImagesEnum";
import { initializeCornerstoneOnce } from "../../../classes/utils/cornerstoneInit";
import "./CornerstoneDicomViewer.css";

const renderingEngineId = "myRenderingEngine";
const viewportId = "IMAGE_STACK";

const getAnatomicalDirection = directionVector => {
  const x = Math.abs(directionVector[0]);
  const y = Math.abs(directionVector[1]);
  const z = Math.abs(directionVector[2]);

  if (x >= y && x >= z) {
    return directionVector[0] > 0 ? "L" : "R";
  } else if (y >= x && y >= z) {
    return directionVector[1] > 0 ? "P" : "A";
  } else {
    return directionVector[2] > 0 ? "S" : "I";
  }
};
const formatDicomImageInfo = position => {
  if (!position) return null;
  const parts = position.split("\\").map(Number);
  return parts;
};

const CornerstoneDicomViewer = ({
  files,
  index,
  onLoadingChange = () => {},
  setOrientations = () => {},
  setImageOrientationState = () => {},
  modality = null,
}) => {
  const [initialized, setInitialized] = useState(false);
  const [stackLoaded, setStackLoaded] = useState(false);
  const [loadedImageCount, setLoadedImageCount] = useState(0);
  const [isPanning, setIsPanning] = useState(false);

  const elementRef = useRef(null);
  const renderingEngineRef = useRef(null);
  const viewportRef = useRef(null);
  const imageUrlsRef = useRef([]);
  const currentImageIdsRef = useRef([]);
  const resizeObserverRef = useRef(null);
  const lastMousePosRef = useRef({ x: 0, y: 0 });

  // Function to handle CT/MR orientation calculation
  const calculateCTMROrientation = async (
    viewport,
    viewPlaneNormal,
    viewUp,
    viewRight
  ) => {
    const rightDirection = getAnatomicalDirection(viewRight);
    const leftDirection = getOppositeDirection(rightDirection);
    const bottomDirection = getOppositeDirection(
      getAnatomicalDirection(viewUp)
    );
    let zDirectionIndex0 = "I";

    try {
      if (currentImageIdsRef.current.length > 0) {
        const firstImage = await imageLoader.loadImage(
          currentImageIdsRef.current[0]
        );

        let orientation = null;
        let firstPos = null;

        const orientationStr = firstImage.data.string(
          DICOM_TAGS.IMAGE_ORIENTATION_PATIENT
        );
        if (orientationStr) {
          orientation = formatDicomImageInfo(orientationStr);
        }

        const positionStr = firstImage.data.string(
          DICOM_TAGS.IMAGE_POSITION_PATIENT
        );
        if (positionStr) {
          firstPos = formatDicomImageInfo(positionStr);
        }

        if (orientation) {
          // Image Orientation Patient: [Xx, Xy, Xz, Yx, Yy, Yz]
          const rowDir = [orientation[0], orientation[1], orientation[2]];
          const colDir = [orientation[3], orientation[4], orientation[5]];

          // Calculate slice normal (cross product)
          const sliceNormal = [
            rowDir[1] * colDir[2] - rowDir[2] * colDir[1],
            rowDir[2] * colDir[0] - rowDir[0] * colDir[2],
            rowDir[0] * colDir[1] - rowDir[1] * colDir[0],
          ];

          // Normalize the slice normal
          const magnitude = Math.sqrt(
            sliceNormal[0] * sliceNormal[0] +
              sliceNormal[1] * sliceNormal[1] +
              sliceNormal[2] * sliceNormal[2]
          );
          const normalizedNormal = [
            sliceNormal[0] / magnitude,
            sliceNormal[1] / magnitude,
            sliceNormal[2] / magnitude,
          ];

          if (currentImageIdsRef.current.length > 1) {
            const lastImage = await imageLoader.loadImage(
              currentImageIdsRef.current[currentImageIdsRef.current.length - 1]
            );

            let lastPos = null;

            const lastPositionStr = lastImage.data.string(
              DICOM_TAGS.IMAGE_POSITION_PATIENT
            );
            if (lastPositionStr) {
              lastPos = formatDicomImageInfo(lastPositionStr);
            }

            if (firstPos && lastPos) {
              // Calculate actual progression vector
              const progressionVector = [
                lastPos[0] - firstPos[0],
                lastPos[1] - firstPos[1],
                lastPos[2] - firstPos[2],
              ];

              // Check if slice normal and progression vector point in same direction
              const dotProduct =
                normalizedNormal[0] * progressionVector[0] +
                normalizedNormal[1] * progressionVector[1] +
                normalizedNormal[2] * progressionVector[2];

              // If dot product is negative, slice normal points opposite to progression
              // This means index 0 is in the opposite direction of slice normal
              const directionForIndex0 =
                dotProduct < 0
                  ? getAnatomicalDirection(normalizedNormal)
                  : getAnatomicalDirection([
                      -normalizedNormal[0],
                      -normalizedNormal[1],
                      -normalizedNormal[2],
                    ]);

              zDirectionIndex0 = directionForIndex0;
            } else {
              // Fallback to just using slice normal
              zDirectionIndex0 = getAnatomicalDirection(normalizedNormal);
            }
          } else {
            // Single slice, use slice normal
            zDirectionIndex0 = getAnatomicalDirection(normalizedNormal);
          }
        } else {
        }
      } else {
      }
    } catch (orientationError) {
      zDirectionIndex0 = getAnatomicalDirection(viewPlaneNormal);
    }

    return {
      left: leftDirection,
      bottom: bottomDirection,
      slide: zDirectionIndex0,
    };
  };

  const calculateDXOrientation = async () => {
    try {
      const firstImage = await imageLoader.loadImage(
        currentImageIdsRef.current[0]
      );

      const patientOrientation = firstImage.data.string(
        DICOM_TAGS.PATIENT_ORIENTATION
      );

      if (patientOrientation) {
        const orientations = patientOrientation.split("\\");
        if (orientations.length >= 2) {
          const rowDirection = orientations[0];
          const columnDirection = orientations[1];

          const leftDirection = mapToLRAPIS(getOppositeDirection(rowDirection));
          const bottomDirection = mapToLRAPIS(columnDirection);

          return {
            left: leftDirection,
            bottom: bottomDirection,
          };
        }
      }

      // Fallback: try Patient Position (0018,5100)
      const patientPosition = firstImage.data.string("x00185100");
      if (patientPosition === "HFS") {
        return { left: "L", bottom: "I" };
      }

      // Default fallback
      return { left: "L", bottom: "I" };
    } catch (error) {
      return { left: "L", bottom: "I" };
    }
  };

  // Updated setImageOrientation function
  const setImageOrientation = useCallback(
    async viewport => {
      if (!viewport) {
        return null;
      }

      if (!currentImageIdsRef.current.length) {
        return null;
      }

      if (!viewport.getCamera) {
        return null;
      }

      if (modality === null) {
        return null;
      }

      try {
        const camera = viewport.getCamera();
        const { viewPlaneNormal, viewUp } = camera;

        const viewRight = [
          viewUp[1] * viewPlaneNormal[2] - viewUp[2] * viewPlaneNormal[1],
          viewUp[2] * viewPlaneNormal[0] - viewUp[0] * viewPlaneNormal[2],
          viewUp[0] * viewPlaneNormal[1] - viewUp[1] * viewPlaneNormal[0],
        ];

        let directions;

        switch (modality) {
          case MODALITY.CT:
          case MODALITY.MR:
            directions = await calculateCTMROrientation(
              viewport,
              viewPlaneNormal,
              viewUp,
              viewRight
            );
            break;
          case MODALITY.DX:
          case MODALITY.CR:
            directions = await calculateDXOrientation();
            break;
          default:
            directions = { left: "L", bottom: "I", slide: "A" };
        }

        setOrientations(directions);
        return directions;
      } catch (error) {
        return null;
      }
    },
    [modality, setOrientations]
  );
  // Initialize engine & viewport once
  useEffect(() => {
    (async () => {
      onLoadingChange(true);
      await initializeCornerstoneOnce();
      const element = elementRef.current;
      if (!element) return;

      const engine = new RenderingEngine(renderingEngineId);
      renderingEngineRef.current = engine;

      engine.enableElement({
        viewportId,
        element,
        type: Enums.ViewportType.STACK,
      });

      viewportRef.current = engine.getViewport(viewportId);
      setInitialized(true);
      onLoadingChange(false);
    })();

    return () => {
      // Cleanup
      imageUrlsRef.current.forEach(URL.revokeObjectURL);
      imageUrlsRef.current = [];
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
      if (renderingEngineRef.current) {
        renderingEngineRef.current.destroy();
      }
      // Purge both Cornerstone caches to prevent memory accumulation across
      // group switches and modal sessions.
      cache.purgeCache();
      cornerstoneDICOMImageLoader.wadouri.dataSetCacheManager.purge();
    };
  }, [onLoadingChange]);

  // Handle viewport resizing
  useEffect(() => {
    const element = elementRef.current;
    const viewport = viewportRef.current;
    const engine = renderingEngineRef.current;

    if (!element || !viewport || !engine) return;

    const handleResize = () => {
      try {
        // Store the current camera state before resizing
        const currentCamera = viewport.getCamera();

        // Resize the viewport to match the new container dimensions
        viewport.setViewport({
          element: element,
        });

        // Restore the previous camera state (zoom level and pan position)
        viewport.setCamera(currentCamera);

        // Re-render the viewport
        engine.renderViewports([viewportId]);
      } catch (error) {}
    };

    // Create ResizeObserver to watch for container size changes
    const resizeObserver = new ResizeObserver(() => {
      // Use requestAnimationFrame to debounce resize events
      requestAnimationFrame(handleResize);
    });

    resizeObserver.observe(element);
    resizeObserverRef.current = resizeObserver;

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
    };
  }, [initialized, stackLoaded]);

  // Mouse interaction handlers for windowing, zoom, and pan
  useEffect(() => {
    const viewport = viewportRef.current;
    const element = elementRef.current;
    const engine = renderingEngineRef.current;
    if (!viewport || !element || !engine || !stackLoaded) return;

    // Pan and windowing controls
    const handleMouseDown = event => {
      if (event.button === 0 && event.ctrlKey) {
        // Ctrl + left click = windowing
        event.preventDefault();
        setIsPanning(false);
        lastMousePosRef.current = { x: event.clientX, y: event.clientY };
        element.style.cursor = "crosshair";
      } else if (event.button === 0 && !event.ctrlKey) {
        // Left click = pan
        event.preventDefault();
        setIsPanning(true);
        lastMousePosRef.current = { x: event.clientX, y: event.clientY };
        element.style.cursor = "grab";
      }
    };

    const handleMouseMove = event => {
      if (event.buttons !== 1) return; // Only when left button is pressed

      const deltaX = event.clientX - lastMousePosRef.current.x;
      const deltaY = event.clientY - lastMousePosRef.current.y;

      if (isPanning && !event.ctrlKey) {
        // Pan the image (fixed X direction)
        const camera = viewport.getCamera();
        const { focalPoint, position } = camera;

        // Calculate pan delta in world coordinates (fixed direction)
        const panScale = 0.5;
        const newFocalPoint = [
          focalPoint[0] - deltaX * panScale,
          focalPoint[1] - deltaY * panScale,
          focalPoint[2],
        ];
        const newPosition = [
          position[0] - deltaX * panScale,
          position[1] - deltaY * panScale,
          position[2],
        ];

        viewport.setCamera({
          focalPoint: newFocalPoint,
          position: newPosition,
        });
      } else if (!isPanning && event.ctrlKey) {
        // Windowing (brightness/contrast)
        const properties = viewport.getProperties();
        const voiRange = properties.voiRange || { lower: 0, upper: 255 };

        // Horizontal movement = window width (contrast)
        const newWidth = Math.max(
          1,
          voiRange.upper - voiRange.lower + deltaX * 2
        );

        // Vertical movement = window level (brightness)
        const currentCenter = (voiRange.upper + voiRange.lower) / 2;
        const newCenter = currentCenter - deltaY * 2;

        const newLower = newCenter - newWidth / 2;
        const newUpper = newCenter + newWidth / 2;

        viewport.setProperties({
          voiRange: { lower: newLower, upper: newUpper },
        });
      }

      engine.renderViewports([viewportId]);
      lastMousePosRef.current = { x: event.clientX, y: event.clientY };
    };

    const handleMouseUp = () => {
      element.style.cursor = "default";
    };

    // Zoom with mouse wheel
    const handleWheel = event => {
      event.preventDefault();

      const camera = viewport.getCamera();
      const { parallelScale } = camera;

      // Zoom in/out based on wheel direction
      const delta = event.deltaY > 0 ? 1.1 : 0.9;
      const newScale = parallelScale * delta;

      viewport.setCamera({
        parallelScale: newScale,
      });

      engine.renderViewports([viewportId]);
    };

    element.addEventListener("mousedown", handleMouseDown);
    element.addEventListener("mousemove", handleMouseMove);
    element.addEventListener("mouseup", handleMouseUp);
    element.addEventListener("mouseleave", handleMouseUp);
    element.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      element.removeEventListener("mousedown", handleMouseDown);
      element.removeEventListener("mousemove", handleMouseMove);
      element.removeEventListener("mouseup", handleMouseUp);
      element.removeEventListener("mouseleave", handleMouseUp);
      element.removeEventListener("wheel", handleWheel);
    };
  }, [stackLoaded, isPanning]);

  // Load stack when files change
  useEffect(() => {
    if (!initialized || !files?.length) {
      setStackLoaded(false);
      setLoadedImageCount(0);
      return;
    }

    (async () => {
      try {
        onLoadingChange(true);
        setStackLoaded(false);

        // Clean up old URLs
        imageUrlsRef.current.forEach(URL.revokeObjectURL);
        imageUrlsRef.current = [];

        // Create new object URLs + IDs
        const urls = files.map(({ file }) => URL.createObjectURL(file));
        imageUrlsRef.current = urls;
        const imageIds = urls.map(url => `wadouri:${url}`);
        currentImageIdsRef.current = imageIds;

        // Images are loaded on-demand by Cornerstone when setStack is called
        // and as the user navigates through slices. The cache (configured in
        // cornerstoneInit.js) uses LRU eviction to manage memory automatically.

        const viewport = viewportRef.current;
        await viewport.setStack(imageIds, 0);
        viewport.resetCamera();
        viewport.render();

        // Calculate and log directions for first slice
        await setImageOrientation(viewport, 0);

        setLoadedImageCount(imageIds.length);
        setStackLoaded(true);
        onLoadingChange(false);
      } catch (error) {
        onLoadingChange(false);
        setStackLoaded(false);
      }
    })();
  }, [initialized, files, onLoadingChange, setImageOrientation]);

  // Update slice when index changes (only after stack loaded)
  useEffect(() => {
    const viewport = viewportRef.current;
    const engine = renderingEngineRef.current;
    if (!viewport || !engine || !stackLoaded || loadedImageCount === 0) {
      return;
    }

    // Clamp index to valid range based on actual loaded images
    const maxIndex = loadedImageCount - 1;
    const clampedIndex = Math.min(Math.max(index, 0), maxIndex);

    try {
      viewport.setImageIdIndex(clampedIndex);
      engine.renderViewports([viewportId]);
    } catch (error) {}
  }, [index, stackLoaded, loadedImageCount]);

  return (
    <div className="cornerstone-viewer-container">
      <div
        ref={elementRef}
        className="cornerstone-canvas"
        style={{ backgroundColor: "#000" }}
      />
    </div>
  );
};

export default CornerstoneDicomViewer;
